import { useEffect } from 'react';
import { RxCross2 } from 'react-icons/rx';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isLoading?: boolean;
    donorName?: string;
}

const DeleteDonationConfirmationModal: React.FC<Props> = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading = false,
    donorName,
}) => {
    useEffect(() => {
        const keyHandler = ({ keyCode }: KeyboardEvent) => {
            if (keyCode === 27 && !isLoading) onClose();
        };
        if (isOpen) {
            document.addEventListener('keydown', keyHandler);
        }
        return () => document.removeEventListener('keydown', keyHandler);
    }, [isOpen, isLoading, onClose]);

    if (!isOpen) return null;

    return (
        <div
            className='fixed left-0 top-0 z-999999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5'
            onClick={() => {
                if (!isLoading) onClose();
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'
            >
                <button
                    className='absolute right-4 top-4 text-gray-600 hover:text-gray-900'
                    onClick={onClose}
                    disabled={isLoading}
                >
                    <RxCross2 />
                </button>
                <div className='p-8'>
                    <h2 className='mb-[30px] text-[22px] font-bold'>
                        Delete donation record?
                    </h2>

                    <p className='mb-[20px]'>
                        {donorName ? (
                            <>
                                Are you sure you want to delete the donation
                                record for <strong>{donorName}</strong>?
                            </>
                        ) : (
                            <>Are you sure you want to delete this donation record?</>
                        )}
                    </p>

                    <p className='mb-[30px]'>
                        This removes the record from the LOC table only.{' '}
                        <strong>
                            It does not refund or remove the charge in Stripe.
                        </strong>{' '}
                        This action cannot be undone.
                    </p>

                    <div className='flex justify-start space-x-4'>
                        <button
                            onClick={onClose}
                            disabled={isLoading}
                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            No, Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={isLoading}
                            className='rounded-full bg-danger px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:bg-red disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            {isLoading ? 'Deleting...' : 'Yes, Delete'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeleteDonationConfirmationModal;
